import { lazy } from 'react';
import { type RouteObject } from 'react-router';

import { FINANCE_NESTED_ROUTES } from './route-tree.nested.finance';

const NewTicketRoute = lazy(() => import('@/routes/tickets/new'));
const TicketsListRoute = lazy(() => import('@/routes/tickets'));
const TicketDetailRoute = lazy(() => import('@/routes/tickets/$ticketId'));
const TicketEditRoute = lazy(() => import('@/routes/tickets/$ticketId-edit'));
const CompaniesRoute = lazy(() => import('@/routes/companies/route'));
const NewCompanyRoute = lazy(() => import('@/routes/companies/new/route'));
const CompanyDetailRoute = lazy(() => import('@/routes/companies/$id/route'));

export const NESTED_ROUTES: Record<string, RouteObject> = {
  'admin/notifications': {
    path: 'admin/notifications',
    lazy: () =>
      import('./routes/admin/notifications/route').then((m) => ({ Component: m.default })),
  },
  tickets: {
    path: 'tickets',
    children: [
      { index: true, element: <TicketsListRoute /> },
      { path: 'new', element: <NewTicketRoute /> },
      { path: ':ticketId', element: <TicketDetailRoute /> },
      { path: ':ticketId/edit', element: <TicketEditRoute /> },
    ],
  },
  inventory: {
    path: 'inventory',
    children: [
      {
        index: true,
        lazy: () => import('./routes/orders/route').then((m) => ({ Component: m.default })),
      },
      {
        path: ':orderId',
        lazy: () => import('./routes/orders/$orderId').then((m) => ({ Component: m.default })),
      },
    ],
  },
  operations: {
    path: 'operations',
    children: [
      {
        index: true,
        lazy: () => import('./routes/operations/route').then((m) => ({ Component: m.default })),
      },
    ],
  },
  'trader-dashboard': {
    path: 'trader-dashboard',
    children: [
      {
        index: true,
        lazy: () =>
          import('./routes/trader-dashboard/route').then((m) => ({ Component: m.default })),
      },
    ],
  },
  'lme-ticker': {
    path: 'lme-ticker',
    lazy: () => import('./routes/lme-ticker/route').then((m) => ({ Component: m.default })),
  },
  'bl-level': {
    path: 'bl-level',
    children: [
      {
        index: true,
        lazy: () => import('./routes/bl-level/route').then((m) => ({ Component: m.default })),
      },
      {
        path: ':blOrderId/edit',
        lazy: () =>
          import('./routes/bl-level/$blOrderId-edit').then((m) => ({ Component: m.default })),
      },
    ],
  },
  claims: {
    path: 'claims',
    children: [
      {
        index: true,
        lazy: () => import('./routes/claims/route').then((m) => ({ Component: m.default })),
      },
      {
        path: ':id',
        lazy: () => import('./routes/claims/$id').then((m) => ({ Component: m.default })),
      },
    ],
  },
  'settings/approval-rules': {
    path: 'settings/approval-rules',
    children: [
      {
        index: true,
        lazy: () =>
          import('./routes/settings/approval-rules/route').then((m) => ({ Component: m.default })),
      },
      {
        path: 'new',
        lazy: () =>
          import('./routes/settings/approval-rules/new/route').then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: ':id',
        lazy: () =>
          import('./routes/settings/approval-rules/$id/route').then((m) => ({
            Component: m.default,
          })),
      },
    ],
  },
  'settings/locations': {
    path: 'settings/locations',
    children: [
      {
        index: true,
        lazy: () =>
          import('./routes/settings/locations/route').then((m) => ({ Component: m.default })),
      },
      {
        path: 'new',
        lazy: () =>
          import('./routes/settings/locations/new/route').then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: ':id',
        lazy: () =>
          import('./routes/settings/locations/$id/route').then((m) => ({
            Component: m.default,
          })),
      },
    ],
  },
  shipments: {
    path: 'shipments',
    lazy: () => import('./routes/shipments/route').then((m) => ({ Component: m.default })),
  },
  approvals: {
    path: 'approvals',
    lazy: () => import('./routes/approvals/route').then((m) => ({ Component: m.default })),
  },
  'payment-approvals': {
    path: 'payment-approvals',
    lazy: () => import('./routes/payment-approvals/route').then((m) => ({ Component: m.default })),
  },
  documentation: {
    path: 'documentation',
    children: [
      {
        index: true,
        lazy: () => import('./routes/documentation/route').then((m) => ({ Component: m.default })),
      },
      {
        path: 'new',
        lazy: () =>
          import('./routes/documentation/new/route').then((m) => ({ Component: m.default })),
      },
      {
        path: ':id/edit',
        lazy: () =>
          import('./routes/documentation/$id-edit/route').then((m) => ({ Component: m.default })),
      },
    ],
  },
  hedging: {
    path: 'hedging',
    children: [
      {
        index: true,
        lazy: () => import('./routes/hedging/route').then((m) => ({ Component: m.default })),
      },
      {
        path: 'prompt/:promptKey',
        lazy: () => import('./routes/hedging/$promptKey').then((m) => ({ Component: m.default })),
      },
    ],
  },
  stonex: {
    path: 'stonex',
    lazy: () => import('./routes/stonex/route').then((m) => ({ Component: m.default })),
  },
  companies: {
    path: 'companies',
    children: [
      { index: true, element: <CompaniesRoute /> },
      { path: 'new', element: <NewCompanyRoute /> },
      { path: ':id', element: <CompanyDetailRoute /> },
    ],
  },
  signing: {
    path: 'signing',
    lazy: () => import('./routes/signing/route').then((m) => ({ Component: m.default })),
  },
  ...FINANCE_NESTED_ROUTES,
};
